import { Box, Stack, type SxProps, Tooltip, Typography, useTheme } from '@mui/material';
import { Cell, Pie, PieChart as RechartsPieChart, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useGetUserNode } from '/@/hooks/nodes';
import { getColor } from '/@/views/frost-risk/frost-risk-map/gdu-forecast';
import colors from '../../theme-provider/colors';

const Label = ({
    userNodeId,
    count,
    color,
    tooltipLabel,
}: {
    userNodeId: number;
    count: number;
    color: string;
    tooltipLabel: string;
}) => {
    const userNodeQuery = useGetUserNode(userNodeId);
    return (
        <Tooltip
            title={
                <Stack>
                    <Typography variant='caption' fontWeight='bold'>
                        {userNodeQuery.data?.name}
                    </Typography>
                    <Typography variant='caption'>
                        {count} {tooltipLabel}
                    </Typography>
                </Stack>
            }
        >
            <Stack direction='row' spacing={1} alignItems='center' justifyContent='start'>
                <Box width={6} height={6} borderRadius='50%' bgcolor={color} />
                <Stack direction='row' spacing={0.5}>
                    <Typography variant='body2' color='primary'>
                        {userNodeQuery.data?.name}
                    </Typography>
                    <Typography variant='body2' color='primary' fontWeight='bold'>
                        {count}
                    </Typography>
                </Stack>
            </Stack>
        </Tooltip>
    );
};

interface TooltipPayload {
    payload?: {
        userNodeId: number;
        count: number;
    };
    value?: number | string;
}

const TooltipContent = ({ payload, tooltipLabel }: { payload?: unknown; tooltipLabel: string }) => {
    const theme = useTheme();
    const typedPayload = payload as TooltipPayload[] | undefined;
    const userNodeId = typedPayload?.[0]?.payload?.userNodeId;
    const userNodeQuery = useGetUserNode(userNodeId ?? 0);

    if (!userNodeQuery.data || !typedPayload?.[0]) {
        return null;
    }

    const value = typedPayload[0].value;
    const displayValue = typeof value === 'number' ? value : Number(value) || 0;

    return (
        <Box
            sx={{
                px: 1,
                py: 0.5,
                backgroundColor: theme.palette.grey[700],
                borderRadius: 1,
                fontSize: '0.75rem',
                color: theme.palette.common.white,
                boxShadow: theme.shadows[8],
                ...(theme.components?.MuiTooltip?.styleOverrides?.tooltip as React.CSSProperties),
            }}
        >
            <Stack spacing={0.25}>
                <Typography
                    variant='caption'
                    sx={{
                        color: theme.palette.common.white,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                    }}
                >
                    {userNodeQuery.data.name}
                </Typography>
                <Typography
                    variant='caption'
                    sx={{
                        color: theme.palette.common.white,
                        fontSize: '0.75rem',
                    }}
                >
                    {displayValue} {tooltipLabel}
                </Typography>
            </Stack>
        </Box>
    );
};

interface PieChartProps {
    data: Array<{ userNodeId: number; count: number }>;
    tooltipLabel: string;
    totalCount: number;
    showLegend?: boolean;
    colorMap?: Map<number, string>;
    sx?: SxProps;
}

const EMPTY_STATE_DATA = [{ userNodeId: -1, count: 1 }];

const PieChart = ({ data, tooltipLabel, totalCount, showLegend = true, colorMap, sx }: PieChartProps) => {
    const isEmpty = totalCount === 0;
    const chartData = isEmpty ? EMPTY_STATE_DATA : data;

    return (
        <>
            <Stack position='relative' sx={sx}>
                <ResponsiveContainer width='100%' aspect={1}>
                    <RechartsPieChart data={chartData}>
                        {!isEmpty && (
                            <RechartsTooltip
                                content={({ payload }) => (
                                    <TooltipContent payload={payload} tooltipLabel={tooltipLabel} />
                                )}
                            />
                        )}
                        <Pie
                            data={chartData}
                            dataKey='count'
                            nameKey='userNodeId'
                            cx='50%'
                            cy='50%'
                            outerRadius='100%'
                            innerRadius='70%'
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={entry.userNodeId}
                                    fill={
                                        isEmpty
                                            ? colors.gray[200]
                                            : (colorMap?.get(entry.userNodeId) ?? getColor(index) ?? '#3498DB')
                                    }
                                />
                            ))}
                        </Pie>
                    </RechartsPieChart>
                </ResponsiveContainer>
                <Typography
                    variant='h4'
                    color='primary'
                    fontWeight='bold'
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                    }}
                >
                    {totalCount}
                </Typography>
            </Stack>
            {showLegend && (
                <Stack spacing={1} direction='row' justifyContent='space-between' flexWrap='wrap' gap={1}>
                    {data.map((item, index) => (
                        <Label
                            key={item.userNodeId}
                            userNodeId={item.userNodeId}
                            count={item.count}
                            color={colorMap?.get(item.userNodeId) ?? getColor(index) ?? '#3498DB'}
                            tooltipLabel={tooltipLabel}
                        />
                    ))}
                </Stack>
            )}
        </>
    );
};

export default PieChart;
